import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ProjectActorVoiceListItemDto } from "Infrastructure/Api/Api";
import { VoiceLlm, VoiceStyle } from "Infrastructure/Api/Api";
import { speakerColors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type {
  JobScriptFE,
  VoiceSegmentFE,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { useScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { parseSegmentText } from "Projects/Utils/parseSegmentText";
import {
  applyStyleToRange,
  buildEditModeText,
  flattenSpans,
  getEmotionMeta,
  getSelectionCharOffsets,
  insertReactionAtOffset,
  isReactionSpan,
  labelOfReactionSpan,
  reconciledSpansFromEditText,
  resolveReactionFromLabel,
  stripReactionBrackets,
} from "Projects/Utils/spanUtils";
import { syncVariablesConservative } from "Projects/Utils/syncVariables";
import {
  getSegmentErrors,
  type ScriptValidationError,
} from "Projects/Utils/validateScript";
import { AddVariableDialog } from "../AddVariableDialog";
import { EmotionBubble } from "../EmotionBubble";
import { ReactionChip } from "../ReactionChip";
import { VariableChip } from "../VariableChip";

interface VoiceSegmentNodeProps {
  index: number;
  segment: VoiceSegmentFE;
  actorVoices?: ProjectActorVoiceListItemDto[];
  validationErrors?: ScriptValidationError[];
  /** Border-left accent color for this actor */
  actorColor?: string;
}

export const VoiceSegmentNode = ({
  index,
  segment,
  actorVoices,
  validationErrors = [],
  actorColor = speakerColors[0],
}: VoiceSegmentNodeProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const {
    removeSegment,
    selectedSegmentIndex,
    focusSegment,
    reactionEditor,
    openReactionEditor,
    setActiveEditSegmentIndex,
    script,
    setScript,
    updateSegment,
    wrapSelectionWithVariable,
    activeEditRef,
    isEditable,
  } = useScriptEditor();

  const [isEditing, setIsEditing] = useState(false);
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [varDialogOpen, setVarDialogOpen] = useState(false);
  const [varSelectedText, setVarSelectedText] = useState("");
  const [pendingVarState, setPendingVarState] = useState<{
    text: string;
    selStart: number;
    selEnd: number;
  } | null>(null);
  const editRef = useRef<HTMLDivElement>(null);
  const segmentSpansRef = useRef<VoiceSegmentFE["spans"]>(segment.spans);
  segmentSpansRef.current = segment.spans;
  const spansAtEditStart = useRef<VoiceSegmentFE["spans"]>(segment.spans);
  const isBeingSplitRef = useRef(false);
  // `useResource().t` returns a fresh function each render. Stash it in a ref
  // so the populate effect (which intentionally only fires on isEditing) can
  // resolve reaction labels without listing `t` in its deps.
  const tRef = useRef(t);
  tRef.current = t;

  const flatText = useMemo(() => flattenSpans(segment.spans), [segment.spans]);
  const varNames = useMemo(
    () => new Set(script.variables.map((v) => v.name)),
    [script.variables],
  );

  const isSelected = selectedSegmentIndex === index;
  const segErrors = getSegmentErrors(validationErrors, index);
  const hasError = segErrors.length > 0;
  const hasEmptyTextError = segErrors.some((e) => e.type === "emptyText");

  const actorVoice = useMemo(
    () => actorVoices?.find((av) => av.actorVoiceID === segment.actorVoiceID),
    [actorVoices, segment.actorVoiceID],
  );
  const actorName = actorVoice?.actorName ?? null;
  const voiceName = actorVoice?.voiceName ?? null;

  // Focus & populate edit area when editing starts. Reactions are rendered as
  // inline `[Label]` tokens so the user sees where they are anchored.
  useEffect(() => {
    if (isEditing && editRef.current) {
      spansAtEditStart.current = segmentSpansRef.current;
      editRef.current.innerText = buildEditModeText(
        segmentSpansRef.current,
        (s) => labelOfReactionSpan(s, tRef.current),
      );
      editRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(editRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setSelectionRect(null);
      return;
    }

    const handleSelectionChange = () => {
      const el = editRef.current;
      if (!el) {
        setSelectionRect(null);
        return;
      }
      const sel = window.getSelection();
      if (
        !sel ||
        sel.rangeCount === 0 ||
        sel.isCollapsed ||
        !el.contains(sel.anchorNode)
      ) {
        setSelectionRect(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0) {
        setSelectionRect(null);
        return;
      }
      setSelectionRect(rect);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [isEditing]);

  /**
   * Flushes pending edit-mode text AND inserts an empty reaction span at the
   * given clean-text cursor position — as a single setState. Returns the
   * index at which the reaction span landed in the resulting span array so
   * the caller can open the reaction editor against it immediately.
   *
   * Must be synchronous w.r.t. its return value — React 18 batches state
   * updates and only eagerly runs the first updater in an event handler, so
   * relying on a second setScriptState updater to populate a closure-bound
   * result variable leaves it stale (returned as -1).
   */
  const commitAndInsertReactionAtCursor = useCallback(
    (cursor: number): number => {
      const rawText = editRef.current?.innerText ?? "";
      const newText = rawText === "\n" ? "" : rawText;
      const committed = reconciledSpansFromEditText(
        spansAtEditStart.current,
        newText,
        (s) => labelOfReactionSpan(s, t),
        (label) => resolveReactionFromLabel(label, t),
      );
      const { spans: newSpans, insertedIndex } = insertReactionAtOffset(
        committed,
        cursor,
      );
      const updatedSegment: VoiceSegmentFE = { ...segment, spans: newSpans };
      const newSegments = [...script.segments];
      newSegments[index] = updatedSegment;
      const scriptWithUpdate: JobScriptFE = {
        ...script,
        segments: newSegments,
      };
      const syncedVariables = syncVariablesConservative(scriptWithUpdate);
      setScript({ ...scriptWithUpdate, variables: syncedVariables });
      setIsEditing(false);
      setSelectionRect(null);
      return insertedIndex;
    },
    [index, segment, script, setScript, t],
  );

  // Register active-edit handlers. `getTextAndSelection` returns the clean
  // (bracket-free) text + clean-text cursor offsets so toolbar actions like
  // split / add variable / add reaction operate on the same coordinate system
  // as the stored spans.
  useEffect(() => {
    if (isEditing) {
      activeEditRef.current = {
        segmentIndex: index,
        getTextAndSelection: () => {
          const el = editRef.current;
          if (!el)
            return {
              text: flatText,
              selStart: flatText.length,
              selEnd: flatText.length,
            };
          const editText = el.innerText;
          const offsets = getSelectionCharOffsets(el);
          const editStart = offsets?.selStart ?? editText.length;
          const editEnd = offsets?.selEnd ?? editText.length;
          const { cleanText, cleanAt } = stripReactionBrackets(editText);
          return {
            text: cleanText,
            selStart: cleanAt(editStart),
            selEnd: cleanAt(editEnd),
          };
        },
        cancelEdit: () => {
          isBeingSplitRef.current = true;
          setIsEditing(false);
          setSelectionRect(null);
        },
        commitAndInsertReactionAtCursor,
      };
      setActiveEditSegmentIndex(index);
    } else if (activeEditRef.current?.segmentIndex === index) {
      activeEditRef.current = null;
      setActiveEditSegmentIndex(null);
    }
  }, [
    isEditing,
    index,
    activeEditRef,
    flatText,
    setActiveEditSegmentIndex,
    commitAndInsertReactionAtCursor,
  ]);

  const handleDisplayClick = useCallback(() => {
    focusSegment(index);
    if (isEditable) {
      setIsEditing(true);
    }
  }, [index, focusSegment, isEditable]);

  const commitEdit = useCallback(() => {
    const rawText = editRef.current?.innerText ?? "";
    const newText = rawText === "\n" ? "" : rawText;
    const newSpans = reconciledSpansFromEditText(
      spansAtEditStart.current,
      newText,
      (s) => labelOfReactionSpan(s, t),
      (label) => resolveReactionFromLabel(label, t),
    );
    const updatedSegment: VoiceSegmentFE = {
      ...segment,
      spans: newSpans,
    };

    const newSegments = [...script.segments];
    newSegments[index] = updatedSegment;

    const scriptWithUpdate: JobScriptFE = {
      ...script,
      segments: newSegments,
    };
    const syncedVariables = syncVariablesConservative(scriptWithUpdate);

    setScript({ ...scriptWithUpdate, variables: syncedVariables });
    setIsEditing(false);
    setSelectionRect(null);
  }, [index, segment, script, setScript, t]);

  const handleBlur = useCallback(() => {
    if (varDialogOpen || isBeingSplitRef.current) {
      isBeingSplitRef.current = false;
      return;
    }
    commitEdit();
  }, [commitEdit, varDialogOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        commitEdit();
      }
    },
    [commitEdit],
  );

  const handleSegmentClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      focusSegment(index);
    },
    [index, focusSegment],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeSegment(index);
    },
    [index, removeSegment],
  );

  /**
   * Apply an emotion to the currently selected range in the contenteditable.
   * Selection offsets from the DOM include bracket characters — convert to
   * clean-text offsets before re-interpreting the spans.
   */
  const handleEmotionSelect = useCallback(
    (style: VoiceStyle | null) => {
      const el = editRef.current;
      if (!el) return;

      const offsets = getSelectionCharOffsets(el);
      if (!offsets) {
        setSelectionRect(null);
        return;
      }
      const editText = el.innerText;
      const { cleanAt } = stripReactionBrackets(editText);
      const selStart = cleanAt(offsets.selStart);
      const selEnd = cleanAt(offsets.selEnd);

      if (selStart >= selEnd) {
        setSelectionRect(null);
        return;
      }

      const currentSpans = reconciledSpansFromEditText(
        spansAtEditStart.current,
        editText,
        (s) => labelOfReactionSpan(s, t),
        (label) => resolveReactionFromLabel(label, t),
      );
      const normalised = style === VoiceStyle.Neutral ? null : style;
      const newSpans = applyStyleToRange(
        currentSpans,
        selStart,
        selEnd,
        normalised,
      );

      spansAtEditStart.current = newSpans;
      updateSegment(index, { ...segment, spans: newSpans });
      setSelectionRect(null);
    },
    [index, segment, updateSegment, t],
  );

  const handleAddVariable = useCallback(() => {
    const el = editRef.current;
    if (!el) return;

    const editText = el.innerText;
    const offsets = getSelectionCharOffsets(el);
    const editStart = offsets?.selStart ?? editText.length;
    const editEnd = offsets?.selEnd ?? editText.length;
    const { cleanText, cleanAt } = stripReactionBrackets(editText);
    const selStart = cleanAt(editStart);
    const selEnd = cleanAt(editEnd);
    const selectedText = cleanText.slice(selStart, selEnd).trim();

    setPendingVarState({ text: cleanText, selStart, selEnd });
    setVarSelectedText(selectedText);
    setVarDialogOpen(true);
  }, []);

  const handleVariableConfirm = useCallback(
    (name: string, _defaultValue: string) => {
      if (pendingVarState) {
        wrapSelectionWithVariable(
          index,
          pendingVarState.text,
          pendingVarState.selStart,
          pendingVarState.selEnd,
          name,
        );
      }
      setPendingVarState(null);
      setVarDialogOpen(false);
    },
    [index, pendingVarState, wrapSelectionWithVariable],
  );

  const borderColor = hasError
    ? theme.palette.error.main
    : isSelected
      ? theme.palette.primary.main
      : theme.colors.border;

  const accentColor = hasError ? theme.palette.error.main : actorColor;

  return (
    <Box
      onClick={handleSegmentClick}
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        position: "relative",
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: "6px",
        minWidth: "160px",
        maxWidth: "none",
        width: "auto",
        m: 0.5,
        backgroundColor: isSelected
          ? `${theme.palette.primary.main}11`
          : theme.colors.backgroundTile,
        overflow: "hidden",
        cursor: "default",
        transition: "border-color 150ms, background-color 150ms",
        "&:hover .segment-delete-btn": { opacity: 1 },
        verticalAlign: "top",
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: "200px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          pt: 0.5,
          pb: 0.25,
          borderBottom: `1px solid ${theme.colors.border}`,
          backgroundColor: theme.colors.backgroundInput,
        }}
      >
        <PersonIcon sx={{ fontSize: 11, color: "text.secondary" }} />
        <Typography
          sx={{
            fontSize: "10px",
            color: actorName
              ? theme.palette.text.secondary
              : theme.palette.error.main,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
        >
          {actorName
            ? voiceName
              ? `${actorName} – ${voiceName}`
              : actorName
            : t(Resources.JobDetail.SelectActorFirst)}
        </Typography>
        {isEditable && (
          <IconButton
            className="segment-delete-btn"
            size="small"
            onClick={handleRemove}
            sx={{
              ml: "auto",
              opacity: 0,
              p: 0.25,
              color: "text.secondary",
              transition: "opacity 150ms",
              "&:hover": { color: "error.main" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>

      {isEditing && (
        <Box
          ref={editRef}
          component="div"
          contentEditable="plaintext-only"
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          sx={{
            px: 1.5,
            py: 0.75,
            fontSize: "13px",
            lineHeight: 1.6,
            color: theme.palette.text.primary,
            outline: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />
      )}

      {!isEditing && (
        <Box
          onClick={handleDisplayClick}
          sx={{
            px: 1.5,
            py: 0.75,
            fontSize: "13px",
            lineHeight: 1.6,
            color: flatText.trim()
              ? theme.palette.text.primary
              : hasEmptyTextError
                ? theme.palette.error.main
                : theme.palette.text.disabled,
            cursor: "text",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {(() => {
            const hasAnyContent =
              flatText.trim().length > 0 ||
              segment.spans.some((s) => isReactionSpan(s));
            if (!hasAnyContent) {
              return hasEmptyTextError
                ? t(Resources.JobDetail.SegmentEmptyText)
                : "…";
            }
            const isV3Voice = actorVoice?.voiceLlm === VoiceLlm.ElevenLabs_V3;
            return segment.spans.map((span, spanIdx) => {
              if (isReactionSpan(span)) {
                return (
                  <ReactionChip
                    // biome-ignore lint/suspicious/noArrayIndexKey: static span order
                    key={spanIdx}
                    span={span}
                    isSelected={
                      reactionEditor?.segmentIndex === index &&
                      reactionEditor?.spanIndex === spanIdx
                    }
                    isReadOnly={!isV3Voice}
                    onClick={() => {
                      focusSegment(index);
                      if (isV3Voice) openReactionEditor(index, spanIdx, false);
                    }}
                  />
                );
              }
              const meta = getEmotionMeta(span.style);
              const hasEmotion =
                span.style != null && span.style !== VoiceStyle.Neutral;
              const tokens = parseSegmentText(span.text);

              return (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: static span order
                  key={spanIdx}
                  style={{
                    backgroundColor: meta.color,
                    borderRadius: hasEmotion ? "3px" : undefined,
                    padding: hasEmotion ? "0 1px" : undefined,
                  }}
                >
                  {tokens.map((token, i) =>
                    token.kind === "text" ? (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static token order
                      <span key={i}>{token.text}</span>
                    ) : (
                      <VariableChip
                        // biome-ignore lint/suspicious/noArrayIndexKey: static token order
                        key={i}
                        name={token.name}
                        isMissing={!varNames.has(token.name)}
                      />
                    ),
                  )}
                  {hasEmotion && (
                    <span
                      style={{
                        fontSize: "11px",
                        marginLeft: "2px",
                        userSelect: "none",
                      }}
                      aria-hidden="true"
                    >
                      {meta.emoji}
                    </span>
                  )}
                </span>
              );
            });
          })()}
        </Box>
      )}

      {isEditing && selectionRect && (
        <EmotionBubble
          anchorRect={selectionRect}
          onSelectStyle={handleEmotionSelect}
          onAddVariable={handleAddVariable}
          onClose={() => setSelectionRect(null)}
        />
      )}

      <AddVariableDialog
        open={varDialogOpen}
        selectedText={varSelectedText}
        onConfirm={handleVariableConfirm}
        onClose={() => {
          setVarDialogOpen(false);
          setPendingVarState(null);
        }}
      />
    </Box>
  );
};
